import { AspectRatio, Box, Container, Image, StackProps, useTheme } from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Slide } from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { ImageBanner } from 'src/components/atoms';
import { FACEBOOK, CRAIGSLIST, GOOGLE, YELP, OTHER } from 'src/constants/marketplace';
import { srcFromCDN } from 'src/utils/files';
import { DEFAULT_IMAGE } from 'src/constants/config';
import PdfRenderer from 'src/components/atoms/pdf-renderer';
import { ListingImage, MarketplaceInfo } from 'src/api/v1-api';
import { FilePath } from 'src/components/organisms/listing-form';
import { isSampleListing } from 'src/utils/marketplaceUtils';

interface GalleryProps {
  images?: ListingImage[];
  aspectRatio?: number | { base?: number; md?: number; lg?: number };
  rootProps?: StackProps;
  listingSource?: string;
  mediaFiles?: FilePath[];
  isAttributeGallery?: boolean;
  marketplace?: MarketplaceInfo | null;
}

interface GallerySlide {
  src?: string;
  sources?: { src: string; type: string }[];
  type?: 'image' | 'video' | 'pdf';
  width?: number;
  height?: number;
}

const ListingGallery = (props: GalleryProps): JSX.Element => {
  const { images, listingSource, mediaFiles, isAttributeGallery, marketplace, rootProps } = props;
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [shouldRenderLightbox, setShouldRenderLightbox] = useState(false);
  const [lightboxModules, setLightboxModules] = useState<{
    Lightbox: typeof import('yet-another-react-lightbox').default;
    Inline: typeof import('yet-another-react-lightbox/plugins/inline').default;
    Zoom: typeof import('yet-another-react-lightbox/plugins/zoom').default;
    Video: typeof import('yet-another-react-lightbox/plugins/video').default;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const theme = useTheme();

  const toggleCarouselOpen = (state: boolean) => () => setCarouselOpen(state);
  const updateIndex = ({ index: current }: { index: number }) => setCarouselIndex(current);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (shouldRenderLightbox) return;

    const target = containerRef.current;
    if (!target) return;

    if (!('IntersectionObserver' in window)) {
      setShouldRenderLightbox(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldRenderLightbox(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: '200px' },
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [shouldRenderLightbox]);

  useEffect(() => {
    if (!shouldRenderLightbox || lightboxModules) return;
    let mounted = true;

    (async () => {
      try {
        const [lightbox, inlinePlugin, zoomPlugin, videoPlugin] = await Promise.all([
          import('yet-another-react-lightbox'),
          import('yet-another-react-lightbox/plugins/inline'),
          import('yet-another-react-lightbox/plugins/zoom'),
          import('yet-another-react-lightbox/plugins/video'),
        ]);

        if (!mounted) return;

        setLightboxModules({
          Lightbox: lightbox.default,
          Inline: inlinePlugin.default,
          Zoom: zoomPlugin.default,
          Video: videoPlugin.default,
        });
      } catch (error) {
        console.error('Failed to load listing gallery modules', error);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [lightboxModules, shouldRenderLightbox]);

  const processImages = useMemo(() => {
    const defaultSlide = [
      {
        src: DEFAULT_IMAGE,
        type: 'image' as const,
      },
    ];

    if (!images || images.length === 0) {
      return defaultSlide;
    }

    return images
      .sort((a, b) => a.position - b.position)
      .map((image) => {
        if (!image.image) {
          return null;
        }

        const lowerCaseImage = image.image.toLowerCase();
        const urlParts = lowerCaseImage.split('?')[0]; // Removes any query parameters
        const videoRegex = /\.(mp4|mov|avi|wmv|flv|mkv|webm|mpeg|avchd|3gp)$/i;
        const isVideo = videoRegex.test(urlParts);

        if (isVideo) {
          return {
            type: 'video' as const,
            sources: [
              {
                src: srcFromCDN(image.image, 1280, 'generic'),
                type: 'video/mp4',
              },
            ],
          };
        } else {
          return { src: srcFromCDN(image.image, 516), type: 'image' as const };
        }
      })
      .filter((item) => item !== null) as GallerySlide[];
  }, [images]);

  const processMedia = useMemo(() => {
    if (!mediaFiles || mediaFiles.length === 0) {
      return [];
    }

    return mediaFiles
      .map((mediaFile) => {
        if (mediaFile && mediaFile.value) {
          const lowerCaseImage = mediaFile.value.toLowerCase();
          const urlParts = lowerCaseImage?.split('?')[0]; // Removes any query parameters
          const videoRegex = /\.(mp4|mov|avi|wmv|flv|mkv|webm|mpeg|avchd|3gp)$/i;
          const isVideo = videoRegex.test(urlParts);

          const pdfRegex = /\.(pdf)$/i;
          const isPdf = pdfRegex.test(urlParts);

          if (isVideo) {
            return {
              type: 'video' as const,
              sources: [
                {
                  src: srcFromCDN(mediaFile.value, 1280, 'generic'),
                  type: 'video/mp4',
                },
              ],
            };
          } else if (isPdf) {
            return { src: mediaFile.value, type: 'pdf' as const };
          } else {
            return {
              src: srcFromCDN(mediaFile.value, 516),
              type: 'image' as const,
            };
          }
        }
        return null;
      })
      .filter((item) => item !== null) as GallerySlide[];
  }, [mediaFiles]);

  const carouselImages = useMemo(() => {
    const mediaFilesList = processMedia;

    if (isAttributeGallery) {
      return [...mediaFilesList];
    } else {
      return [...processImages, ...mediaFilesList];
    }
  }, [processImages, processMedia, isAttributeGallery]);

  const previewSlide = useMemo(() => {
    const allSlides = carouselImages as GallerySlide[];
    const firstImage = allSlides.find((slide) => slide.type === 'image' && slide.src);
    return firstImage ?? allSlides[0];
  }, [carouselImages]);

  const inlinePlugins = useMemo(() => {
    if (!lightboxModules) return [];
    return [lightboxModules.Inline, lightboxModules.Video];
  }, [lightboxModules]);

  const modalPlugins = useMemo(() => {
    if (!lightboxModules) return [];
    return [lightboxModules.Zoom, lightboxModules.Video];
  }, [lightboxModules]);

  const LightboxComponent = lightboxModules?.Lightbox;

  return (
    <Container
      ref={containerRef}
      maxW="516px"
      maxH="516px"
      px={0}
      position="relative"
      overflow="hidden"
      {...rootProps}
    >
      {isSampleListing(marketplace ?? null, listingSource ?? '') ? (
        <ImageBanner text="Sample Listing" bgColor="#718096" />
      ) : (
        <>
          {listingSource === CRAIGSLIST && (
            <ImageBanner text="Spotted on Craigslist" bgColor="#718096" />
          )}
          {listingSource === FACEBOOK && (
            <ImageBanner text="Spotted on Facebook" bgColor="#718096" />
          )}
          {listingSource === GOOGLE && <ImageBanner text="Spotted on Google" bgColor="#718096" />}
          {listingSource === YELP && <ImageBanner text="Spotted on Yelp" bgColor="#718096" />}
          {listingSource === OTHER && <ImageBanner text="Spotted on the Web" bgColor="#718096" />}
        </>
      )}
      {shouldRenderLightbox && LightboxComponent && inlinePlugins.length ? (
        <LightboxComponent
          index={carouselIndex}
          slides={carouselImages as Slide[]}
          plugins={inlinePlugins}
          on={{
            view: updateIndex,
            click: toggleCarouselOpen(true),
          }}
          carousel={{
            padding: 0,
            spacing: 0,
            finite: true,
            imageFit: 'contain',
          }}
          inline={{
            style: {
              width: '100%',
              margin: '0',
              padding: '0',
              aspectRatio: 1,
            },
          }}
          styles={{
            root: { '--yarl__color_backdrop': '#E2E8F0' },
            container: { borderRadius: theme.border_radius.border_radius_1 },
          }}
          video={{
            autoPlay: false,
            controls: true,
            loop: true,
            muted: true,
            playsInline: true,
          }}
          render={{
            slide: ({ slide }) =>
              slide.type !== 'image' && slide.type !== 'video' ? (
                <PdfRenderer url={slide.src} />
              ) : null,
            buttonPrev: (carouselImages as Slide[]).length <= 1 ? () => null : undefined,
            buttonNext: (carouselImages as Slide[]).length <= 1 ? () => null : undefined,
          }}
        />
      ) : (
        <AspectRatio ratio={1} width="100%">
          <Box position="relative" width="100%" height="100%">
            <Image
              src={(previewSlide && previewSlide.src) || DEFAULT_IMAGE}
              alt="Listing preview"
              width="100%"
              height="100%"
              objectFit="cover"
              fallbackSrc={DEFAULT_IMAGE}
            />
          </Box>
        </AspectRatio>
      )}
      {shouldRenderLightbox && LightboxComponent && modalPlugins.length ? (
        <LightboxComponent
          open={carouselOpen}
          close={toggleCarouselOpen(false)}
          index={carouselIndex}
          slides={carouselImages as Slide[]}
          on={{ view: updateIndex }}
          animation={{ fade: 0 }}
          controller={{ closeOnPullDown: true, closeOnBackdropClick: true }}
          styles={{ root: { '--yarl__color_backdrop': '#E2E8F0' } }}
          plugins={modalPlugins}
          render={{
            iconZoomIn: () => null,
            iconZoomOut: () => null,
            slide: ({ slide }) =>
              slide.type !== 'image' && slide.type !== 'video' ? (
                <PdfRenderer url={slide.src} />
              ) : null,
            buttonPrev: (carouselImages as Slide[]).length <= 1 ? () => null : undefined,
            buttonNext: (carouselImages as Slide[]).length <= 1 ? () => null : undefined,
          }}
          carousel={{
            finite: true,
          }}
          video={{
            autoPlay: false,
            controls: true,
            loop: true,
            muted: true,
          }}
        />
      ) : null}
    </Container>
  );
};

export default ListingGallery;
